/**
 * RequireMandantAccessForCase — Guard, der prüft ob der eingeloggte User
 * einen aktiven Mandanten-Zugang zu einem konkreten Case hat.
 *
 * Reihenfolge zur Laufzeit:
 *   1. RequireAuth läuft VOR diesem Guard (siehe App.tsx-Verschachtelung).
 *      Daher können wir hier davon ausgehen, dass `user !== null`.
 *   2. Wir rufen hasMandantAccess(caseId) auf — RLS auf case_mandant_access
 *      erlaubt einem Mandanten exakt seinen eigenen Eintrag zu sehen.
 *   3. Bei `false` redirecten wir auf /login (mit Hinweis, dass die
 *      Einladung evtl. abgelaufen ist). Berater-User landen hier auch im
 *      Redirect, weil sie keinen case_mandant_access-Eintrag haben — das
 *      ist Absicht (Berater navigieren über /case/:id/cockpit, nicht über
 *      /mandant/case/:id).
 *
 * Im Mock-Modus (Dev): Wir lassen alles durch — der Mock-Berater darf
 * jeden Case sehen, sonst kann man die Mandanten-Routen lokal nicht
 * entwickeln. Production tree-shake-t den Mock-Pfad weg.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuthOrSession } from '../../lib/authOrSession';
import { hasMandantAccess } from '../../lib/supabaseInvitations';

interface RequireMandantAccessForCaseProps {
  children: ReactNode;
}

type CheckState = { kind: 'checking' } | { kind: 'allowed' } | { kind: 'denied' };

export function RequireMandantAccessForCase({ children }: RequireMandantAccessForCaseProps) {
  // Wir akzeptieren beide URL-Param-Namen:
  //   /mandant/case/:caseId          (Home-Route)
  //   /mandant/case/:id/wizard/:step (Wizard-Routen, derselbe Param-Name wie
  //                                   im Berater-Pfad, damit Step-Komponenten
  //                                   nichts ändern müssen)
  const params = useParams<{ caseId?: string; id?: string }>();
  const caseId = params.caseId ?? params.id ?? '';
  const { isMock, user, isLoading } = useAuthOrSession();
  const [check, setCheck] = useState<CheckState>({ kind: 'checking' });

  useEffect(() => {
    let cancelled = false;
    if (isLoading) return;
    if (!user) {
      // RequireAuth wird hier ohnehin redirecten; wir markieren als denied,
      // damit kein Render-Flash auftaucht.
      setCheck({ kind: 'denied' });
      return;
    }
    // Im Dev-Mock: Zugang immer durchwinken (kein Supabase-Roundtrip möglich).
    if (isMock) {
      setCheck({ kind: 'allowed' });
      return;
    }
    void hasMandantAccess(caseId)
      .then((ok) => {
        if (!cancelled) setCheck({ kind: ok ? 'allowed' : 'denied' });
      })
      .catch(() => {
        if (!cancelled) setCheck({ kind: 'denied' });
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, isLoading, isMock, user]);

  if (isLoading || check.kind === 'checking') {
    return null;
  }
  if (check.kind === 'denied') {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}
